/**
 * InvoiceSettingsSection (BLU-328)
 *
 * Per-location toggle for enabling/disabling order invoice printing.
 * Each toggle owns its own mutation to prevent multi-location lockout.
 */

import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Receipt, Loader2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useLocation } from '@/contexts/LocationContext';

interface InvoiceSettingsSectionProps {
  embedded?: boolean;
  /**
   * Console single-location mode: when set, render only this location's row
   * and omit the per-row location name (the Console section header already
   * names the location). Absent = today's full-list behavior.
   */
  locationId?: string;
}

export function InvoiceSettingsSection({ embedded, locationId }: InvoiceSettingsSectionProps) {
  const { t } = useTranslation('settings');
  const { locations } = useLocation();
  const allProductionLocations = locations ?? [];
  const productionLocations = locationId
    ? allProductionLocations.filter((l) => l.id === locationId)
    : allProductionLocations;

  if (productionLocations.length === 0) {
    return (
      <div className="text-sm text-muted-foreground p-2">
        {t('production.invoice.noLocations')}
      </div>
    );
  }

  const content = (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {t('production.invoice.description')}
      </p>
      {productionLocations.map((location) => (
        <LocationInvoiceToggle
          key={location.id}
          locationId={location.id}
          locationName={location.name}
          showName={!locationId}
        />
      ))}
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Receipt className="h-5 w-5" />
          {t('production.general.invoicePrinting')}
        </CardTitle>
        <CardDescription>
          {t('production.general.invoicePrintingDesc')}
        </CardDescription>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}

function LocationInvoiceToggle({
  locationId,
  locationName,
  showName = true,
}: {
  locationId: string;
  locationName: string;
  /** False in Console single-location mode — the section header already names the location. */
  showName?: boolean;
}) {
  const { t } = useTranslation('settings');
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.productionSettings.getLocationInvoiceEnabled.useQuery(
    { locationId },
    { staleTime: 60 * 1000 }
  );

  // Each toggle owns its own mutation to avoid disabling all toggles when one saves
  const updateMutation = trpc.productionSettings.updateLocationInvoiceEnabled.useMutation({
    onSuccess: () => {
      utils.productionSettings.getLocationInvoiceEnabled.invalidate({ locationId });
      toast.success(t('production.invoice.updated'));
    },
    onError: (err) => {
      toast.error(t('production.invoice.updateFailed'), {
        description: err.message,
      });
    },
  });

  // Match server default (true) once loaded; show loading state while pending
  const enabled = data?.enabled ?? true;
  const isBusy = isLoading || updateMutation.isPending;

  return (
    <div className="flex items-center justify-between p-3 rounded-lg border">
      {/* Single-location mode labels the SETTING (the section header already
          names the location) so the switch never sits alone on its row. */}
      <span className="text-sm font-medium">
        {showName ? locationName : t('production.general.invoicePrinting')}
      </span>
      <div className="flex items-center gap-2">
        {isBusy && (
          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
        )}
        <Switch
          checked={enabled}
          onCheckedChange={(checked) => updateMutation.mutate({ locationId, enabled: checked })}
          disabled={isBusy}
          aria-label={enabled ? t('production.invoice.disableAria', { name: locationName }) : t('production.invoice.enableAria', { name: locationName })}
        />
      </div>
    </div>
  );
}
